import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { canvasService } from '../../services/canvasService';
import { Globe, Lock, Copy, Check, Link2, AlertTriangle } from 'lucide-react';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  canvasId: string;
  canvasName: string;
}

type ShareMode = 'public' | 'password' | null;

export function ShareModal({ isOpen, onClose, canvasId, canvasName }: ShareModalProps) {
  const [shareMode, setShareMode] = useState<ShareMode>(null);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasTemplates, setHasTemplates] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Load current share settings
  useEffect(() => {
    if (isOpen && canvasId) {
      setIsLoading(true);
      setError(null);
      canvasService.getShareSettings(canvasId)
        .then(({ data }: any) => {
          setShareMode(data.shareMode);
          setShareToken(data.shareToken);
          setHasTemplates(data.hasTemplates || false);
        })
        .catch(() => setError('Failed to load share settings'))
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, canvasId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPassword('');
      setCopied(false);
      setError(null);
    }
  }, [isOpen]);

  const shareUrl = shareToken ? `${window.location.origin}/s/${shareToken}` : null;

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSave = async (mode: ShareMode, pw?: string) => {
    setIsSaving(true);
    setError(null);
    try {
      const { data } = await canvasService.updateShare(canvasId, {
        shareMode: mode,
        password: pw,
      });
      setShareMode(data.shareMode);
      setShareToken(data.shareToken);
    } catch (err: any) {
      setError(err.message || 'Failed to update share settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEnablePublic = () => handleSave('public');

  const handleEnablePassword = () => {
    if (!password.trim()) {
      setError('Enter a password');
      passwordRef.current?.focus();
      return;
    }
    handleSave('password', password.trim());
  };

  const handleDisable = () => {
    setPassword('');
    handleSave(null);
  };

  const handleModeSwitch = (newMode: 'public' | 'password') => {
    if (newMode === 'public') {
      handleSave('public');
    } else {
      // Switching to password mode — show password input, don't save yet
      setShareMode('password');
    }
  };

  const isShared = shareMode !== null;

  // Canvas has templates and isn't already shared — block sharing
  const isBlockedByTemplates = hasTemplates && !isShared;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share Canvas" size="sm">
      {isLoading ? (
        <div className="py-8 text-center text-sm text-gray-400">Loading...</div>
      ) : isBlockedByTemplates ? (
        <div className="space-y-3 py-2">
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Can't share this canvas</p>
              <p className="text-xs text-amber-600 mt-1">
                This canvas contains Template nodes that reference other canvases. Remove all Template nodes first, then you can share it.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Current state */}
          {!isShared ? (
            // Not shared yet — show enable options
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Share <span className="font-medium text-gray-700">{canvasName}</span> so others can view and duplicate it.
              </p>

              <button
                onClick={handleEnablePublic}
                disabled={isSaving}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
              >
                <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-4 h-4 text-primary-600" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800">Public</div>
                  <div className="text-xs text-gray-500">Anyone with a Cascady account can view</div>
                </div>
              </button>

              <button
                onClick={() => setShareMode('password')}
                disabled={isSaving}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-amber-300 hover:bg-amber-50 transition-colors text-left"
              >
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800">Password Protected</div>
                  <div className="text-xs text-gray-500">Only people with the password can view</div>
                </div>
              </button>

              {/* Show password input when password mode selected but not yet saved */}
              {shareMode === 'password' && !shareToken && (
                <div className="space-y-2 pt-1">
                  <input
                    ref={passwordRef}
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleEnablePassword()}
                    placeholder="Enter a share password..."
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    autoFocus
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleEnablePassword}
                    isLoading={isSaving}
                    className="w-full"
                  >
                    Enable Sharing
                  </Button>
                </div>
              )}
            </div>
          ) : (
            // Already shared — show link + controls
            <div className="space-y-4">
              {/* Mode indicator */}
              <div className="flex items-center gap-2">
                {shareMode === 'public' ? (
                  <div className="flex items-center gap-1.5 text-sm text-primary-600">
                    <Globe className="w-4 h-4" />
                    <span className="font-medium">Public</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-sm text-amber-600">
                    <Lock className="w-4 h-4" />
                    <span className="font-medium">Password Protected</span>
                  </div>
                )}
                <span className="text-xs text-gray-400 ml-auto">
                  {shareMode === 'public' ? 'Visible to all users' : 'Requires password'}
                </span>
              </div>

              {/* Share link */}
              {shareUrl && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 truncate font-mono">
                    {shareUrl}
                  </div>
                  <Button
                    variant={copied ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={handleCopy}
                    className="flex-shrink-0"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              )}

              {/* Mode switcher */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleModeSwitch('public')}
                  disabled={isSaving}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors ${
                    shareMode === 'public'
                      ? 'bg-primary-100 text-primary-700 border border-primary-200'
                      : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <Globe className="w-3.5 h-3.5" />
                  Public
                </button>
                <button
                  onClick={() => handleModeSwitch('password')}
                  disabled={isSaving}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors ${
                    shareMode === 'password'
                      ? 'bg-amber-100 text-amber-700 border border-amber-200'
                      : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <Lock className="w-3.5 h-3.5" />
                  Password
                </button>
              </div>

              {/* Password update (when in password mode) */}
              {shareMode === 'password' && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-600">Set new password</label>
                  <div className="flex gap-2">
                    <input
                      ref={passwordRef}
                      type="text"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleEnablePassword()}
                      placeholder="Enter password..."
                      className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleEnablePassword}
                      isLoading={isSaving}
                      disabled={!password.trim()}
                    >
                      Update
                    </Button>
                  </div>
                </div>
              )}

              {/* Info */}
              <p className="text-xs text-gray-400">
                Viewers can see your canvas (read-only) and duplicate it into their own workspace.
              </p>

              {/* Disable sharing */}
              <button
                onClick={handleDisable}
                disabled={isSaving}
                className="w-full text-xs text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg py-2 transition-colors"
              >
                Disable Sharing
              </button>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-500 mt-2">{error}</p>
          )}
        </div>
      )}
    </Modal>
  );
}
